import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get, isEmpty, isNil } from 'lodash';
import * as pdfjsLib from 'pdfjs-dist';

import { Button, AcknowledgeButton } from 'components/Button';
import GeneralError from 'components/GeneralError';
import { getIPAddress, nextAction } from 'actions/workflow';
import useWorkflowGuard from 'hooks/useWorkflowGuard';

import './styles.scss'
import Spinner from 'components/Spinner';
import { Maximize2 } from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const ContractSigning = ({ navigate, searchParams, dispatch }) => {
  useWorkflowGuard(searchParams, navigate);
  const [consentChecked, setConsentChecked] = useState(false);
  const [ipAddress, setIpAddress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const workflow = useSelector((state) => get(state, 'workflow'));
  const checkinData = get(workflow, 'data');
  const pdfContainerRef = useRef(null);

  const base64ToArrayBuffer = (data) => {
    if (data) {
      const bytes = new Uint8Array(data.length);
      const arrayBuffer = bytes.map((item, idx) => data.charCodeAt(idx));
      return arrayBuffer;
    }
  };

  const renderAllPages = async (loadingTask) => {
    try {
      const pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;

      const renderPromises = [];

      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        renderPromises.push(pdf.getPage(pageNumber).then((page) => {
          const PDFWidth = page.getViewport({ scale: 1 }).width;
          const scale = 610 / PDFWidth;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.id = `pdf-page-${pageNumber}`;
          canvas.classList.add('pdf-page');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          if (pdfContainerRef.current) {
            pdfContainerRef.current.appendChild(canvas);
          }
          const renderContext = {
            canvasContext: context,
            viewport,
          };
          return page.render(renderContext).promise;
        }));
      }

      await Promise.all(renderPromises);
      setIsPdfLoading(false);
    } catch (error) {
      setIsPdfLoading(false);
      console.error('Error rendering PDF pages:', error);
    }
  };

  const convertPdfUrl = () => {
    const pdfData = window.atob(get(checkinData, 'loanDocument'));
    const type = 'PDF';
    const arrayBuffer = base64ToArrayBuffer(pdfData);
    const file = new Blob([arrayBuffer], { type: `application/${type}` });
    const data = window.URL.createObjectURL(file);
    const loadingTask = pdfjsLib.getDocument({ data: pdfData });
    setPdfUrl(data);
    renderAllPages(loadingTask);
  };

  const fetchIpAddress = () => {
    dispatch(getIPAddress({
      success: (ipResponse) => {
        setIpAddress(ipResponse.ip);
      },
      fail: (err) => {
        setIpAddress('static ip');
        console.log(err, 'Getting IP address failed');
      },
    }));
  }

  useEffect(() => {
    if (!isEmpty(checkinData) && !isNil(checkinData) && get(checkinData, 'loanDocument')) {
      fetchIpAddress();
      convertPdfUrl();
    }
  }, [checkinData]);

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
  }

  const handleSubmit = () => {
    if (!consentChecked) return;

    setIsSubmitting(true);
    dispatch(nextAction({
      key: searchParams.get('key'),
      data: {
        ipAddress,
      },
      success: (res) => {
        const nextUrl = get(res, 'data.url');
        if (nextUrl) navigate(nextUrl);
        setIsSubmitting(false);
      },
      fail: handleFail,
    }));
  };

  return (
    <div className="flex flex-col items-center mb-8">
      <h2 className="text-center text-xl md:text-3xl font-semibold text-slate-800 mb-3">
        You're almost there!
        <br />
        Just click and go!
      </h2>
      <p className="text-slate-600">Please electronically sign your documents</p>

      <div className="border-t border-slate-300 pt-6 mt-8 mb-14">
        <p className="text-slate-700 text-sm leading-relaxed mb-4">
          By clicking the <span className="font-semibold">ACKNOWLEDGE</span> button below and clicking
          {' '}
          <span className="font-semibold">ESIGN LOAN DOCUMENTS</span>
          , you acknowledge that you have read and agree to the
          {' '}
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="underline text-blue-500 font-semibold pointer">Loan Documents</a>
          {' '} and are electronically signing every document contained therein that requires your signature.
        </p>

        <AcknowledgeButton
          className="mb-4 max-w-[375px] mx-auto"
          label="ACKNOWLEDGE"
          checked={consentChecked}
          onChange={setConsentChecked}
        />

        <GeneralError error={generalError} />

        <Button
          className="block w-full max-w-[375px] mx-auto"
          label="ESIGN LOAN DOCUMENTS"
          isLoading={isSubmitting}
          isDisabled={!consentChecked || isPdfLoading}
          onClick={handleSubmit}
        />
      </div>

      <div className="border border-slate-200 rounded-lg w-full p-4">
        <div className="flex justify-between gap-4 items-center mb-5">
          <h2 className="font-semibold">Your Loan Document</h2>
          <button
            className="text-slate-600 hover:text-slate-900 transition"
            type="button"
            onClick={() => window.open(pdfUrl)}
          >
            <Maximize2 size={16} />
          </button>
        </div>

        <div className="p-4 border border-blue-500 rounded-md">
          {isPdfLoading && <Spinner className="mx-auto my-10" color="blue-400" />}
          <div className="pdf-wrapper" ref={pdfContainerRef} />
        </div>
      </div>
    </div>
  );
};

ContractSigning.propTypes = {
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default ContractSigning;
